import React, { useEffect, useState } from 'react'
import './Booking.css'
import Header from '../components/Header'
import axios from 'axios'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js'
import PaymentHistory from './PaymentHistory'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const getAuthToken = () =>
  document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1")

const STATS = (summary, amountPaid) => [
  { icon: '📅', label: 'Total Bookings', value: summary.totalBookings, color: '#60a5fa' },
  { icon: '💰', label: 'Total Revenue', value: `₹${Math.floor(summary.totalRevenue)}`, color: '#34d399' },
  { icon: '📤', label: 'Amount Due', value: `₹${Math.max(0, Math.floor(summary.amountTobePaid - amountPaid))}`, color: '#fb923c' },
  { icon: '✅', label: 'Amount Paid', value: `₹${Math.floor(amountPaid)}`, color: '#a78bfa' },
]

const BookingPage = () => {
  const [bookings, setBookings] = useState([])
  const [chartData, setChartData] = useState(null)
  const [viewMode, setViewMode] = useState('sales')
  const [summary, setSummary] = useState({ totalBookings: 0, totalRevenue: 0, amountTobePaid: 0 })
  const [amountPaid, setAmountPaid] = useState(0)
  const [loading, setLoading] = useState(true)

  const getAllBooking = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/booking`, {
        headers: { auth: getAuthToken() },
      })
      const data = response.data.Booking || []
      setBookings(data)
      generateChartData(data, viewMode)
      calculateSummary(data)
    } catch (error) {
      console.error('Error fetching bookings:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPayments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/banking/get_payment`, {
        headers: { auth: getAuthToken() },
      })
      const payments = response.data.payments || []
      setAmountPaid(payments.reduce((sum, p) => sum + p.amount_paid, 0))
    } catch (error) {
      console.error('Error fetching payments:', error)
    }
  }

  useEffect(() => { getAllBooking(); getPayments() }, [])

  useEffect(() => {
    if (bookings.length) generateChartData(bookings, viewMode)
  }, [viewMode, bookings])

  const calculateSummary = (data) => {
    const totalRevenue = data.reduce((sum, b) => b.isPaid ? sum + b.subscriptionPrice : sum, 0)
    const totalBookings = data.length
    const amountTobePaid = totalRevenue - totalRevenue * 8 / 100
    setSummary({ totalBookings, totalRevenue, amountTobePaid })
  }

  const generateChartData = (data, mode) => {
    const byDate = data.reduce((acc, b) => {
      const date = new Date(b.bookingDate).toLocaleDateString()
      acc[date] = acc[date] || { sales: 0, count: 0 }
      acc[date].sales += b.subscriptionPrice
      acc[date].count += 1
      return acc
    }, {})
    const dates = Object.keys(byDate)
    const values = dates.map(d => byDate[d][mode])
    setChartData({
      labels: dates,
      datasets: [{
        label: mode === 'sales' ? 'Revenue (₹)' : 'Bookings Count',
        data: values,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointRadius: 4,
      }],
    })
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1a2233',
        titleColor: '#94a3b8',
        bodyColor: '#f1f5f9',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.04)' },
        ticks: { color: '#64748b', font: { size: 11 } },
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.04)' },
        ticks: { color: '#64748b', font: { size: 11 } },
      },
    },
  }

  const stats = STATS(summary, amountPaid)

  return (
    <div className="booking-page">
      <Header />
      <div className="bkg-content">
        <div className="bkg-page-header">
          <div>
            <h1 className="bkg-page-title">Booking Analytics</h1>
            <p className="bkg-page-sub">Revenue and booking insights</p>
          </div>
          <button className="bkg-btn-refresh" onClick={() => { getAllBooking(); getPayments() }}>↻ Refresh</button>
        </div>

        <div className="bkg-stats-grid">
          {stats.map((s, i) => (
            <div className="bkg-stat-card" key={i}>
              <div className="bkg-stat-icon" style={{ background: `${s.color}18`, color: s.color }}>{s.icon}</div>
              <div>
                <p className="bkg-stat-label">{s.label}</p>
                <p className="bkg-stat-value" style={{ color: s.color }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bkg-chart-card">
          <div className="bkg-chart-header">
            <h2 className="bkg-chart-title">Revenue Over Time</h2>
            <div className="bkg-chart-tabs">
              <button
                className={`bkg-tab ${viewMode === 'sales' ? 'active' : ''}`}
                onClick={() => setViewMode('sales')}
              >Revenue</button>
              <button
                className={`bkg-tab ${viewMode === 'count' ? 'active' : ''}`}
                onClick={() => setViewMode('count')}
              >Bookings</button>
            </div>
          </div>
          <div className="bkg-chart-body">
            {chartData ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div className="bkg-chart-empty">No data yet</div>
            )}
          </div>
        </div>

        <PaymentHistory summary={summary} />
      </div>
    </div>
  )
}

export default BookingPage
